import { createElement, useState, memo } from "react";
import { camelCase, DynamicFormControl, DynamicFormDefinition, FormSubmission } from "../../";
import DateControl from "./controls/date-control";
import TextControl from "./controls/text-control";
import PhoneControl from "./controls/phone-control";
import SelectControl from "./controls/select-control";
import CheckboxControl from "./controls/checkbox-control";
import MaskedTextControl from "./controls/masked-text-control";
import FileUploadControl from "./controls/file-upload-control";
import HtmlControl from "./controls/html-control";
import RadioButtonControl from "./controls/radio-button-control";
import SubmitButtonControl from "./controls/submit-button-control";
import CancelButtonControl from "./controls/cancel-button-control";
import FormControlContainer from "./controls/form-control-container";
import { Box, Button, Grid } from "@mui/material";
import { useTranslateDetection } from "../helpers/translate-helper";
import SignatureControl from "./controls/signature-control";

const FormContainer = memo(({ form, hideSubmit, formLoading, loading, readOnly, formSubmission, onSave, onCancel }: {
    form: DynamicFormDefinition | null,
    hideSubmit: boolean,
    formLoading: boolean,
    loading: boolean,
    readOnly?: boolean,
    formSubmission?: FormSubmission,
    onSave?: (() => void) | undefined,
    onCancel?: (() => void) | undefined
}) => {

    const controls = new Map([['text', TextControl],
    ['date', DateControl],
    ['phone', PhoneControl],
    ['select', SelectControl],
    ['masked', MaskedTextControl],
    ['checkbox', CheckboxControl],
    ['upload', FileUploadControl],
    ['html', HtmlControl],
    ['signature', SignatureControl],
    ['radio', RadioButtonControl]]);

    const [formControls, setFormControls] = useState(form?.controls);

    // TODO: This will prevent navigation, but need a way to determine if form has been changed
    // useUnload(e => {
    //     e.preventDefault();
    //     e.returnValue = '';
    // });

    // May need to look into it more as to why this is needed. Translations don't seem to work correctly when just using the hook in the components.
    useTranslateDetection();

    return (
        <div>
            <form onSubmit={saveForm} hidden={formLoading}>
                <Grid container spacing={3} direction="row" justifyContent="space-between" alignItems="center">
                    {formControls?.map(x => {
                        x.canDisplay = canDisplayItem(x);

                        return (
                            <FormControlContainer key={x.id + '-container'} control={x} form={form} hideSubmit={hideSubmit} formLoading={formLoading} loading={loading} readOnly={readOnly} formSubmission={formSubmission} onCancel={onCancel} onStateUpdate={onStateUpdate} ></FormControlContainer>
                        )
                    })}
                </Grid>
                <Box style={{ marginTop: 50 }}>
                    {
                        !hideSubmit ?
                            <>
                                <CancelButtonControl style={{ marginRight: 20 }} disabled={formLoading || loading} onCancel={cancel} />
                                <Button key="form-button-submit" variant="contained" disabled={formLoading || loading} color="primary" type="submit">Submit</Button>
                            </> : null
                    }
                    <span style={{ float: 'right', fontStyle: 'italic', opacity: .5 }}>Powered by Rock Solid Software</span>
                </Box>
            </form>
        </div>
    );

    async function saveForm(e: any): Promise<void> {
        e.preventDefault();

        if (onSave) {
            onSave();
        }

        e.target.reset();
    }

    function canDisplayItem(formControl: DynamicFormControl): boolean {
        if (formControl.conditionalConfigurations == null) {
            return true;
        }

        const result = formControl.conditionalConfigurations.every(config => {
            const control = form?.controls.find(x => x.id === config.fieldId);
            if (Array.isArray(control?.state)) {
                return control?.state.some(x => x === config.value);
            } else {
                return control?.state === config.value;
            }
        });

        return result;
    }

    function cancel(): void {
        if (onCancel) {
            onCancel();
        }
    }

    function onStateUpdate(state: string | string[] | null, id: string | undefined) {
        let hasChange = false;

        const newControls = formControls?.map(control => {
            const canDisplay = canDisplayItem(control);
            if (canDisplay != control.canDisplay) {
                control.canDisplay = canDisplay;
                hasChange = true;
            }
            return control;
        });

        if (hasChange) {
            setFormControls(newControls);
        }
    }
});

export default FormContainer;